import { supabase } from '@/lib/supabase'

export async function createCategory(name: string, parentId: string, ownerId?: string | null) {
  const { data: parent, error: parentError } = await supabase
    .from('categories')
    .select('id, parent_id')
    .eq('id', parentId)
    .single()

  if (parentError || !parent) throw new Error('Parent category not found')
  if (parent.parent_id !== null) throw new Error('parent_id must reference a top-level category')

  const { data: siblings } = await supabase
    .from('categories')
    .select('sort_order')
    .eq('parent_id', parentId)
    .order('sort_order', { ascending: false })
    .limit(1)

  const nextSortOrder = (siblings && siblings.length > 0 ? (siblings[0].sort_order ?? 0) : 0) + 1

  const { data, error } = await supabase
    .from('categories')
    .insert({
      name,
      parent_id: parentId,
      is_system: false,
      sort_order: nextSortOrder,
      owner_id: ownerId ?? null,
    })
    .select()
    .single()

  if (error) throw new Error(error.message)
  return data
}

export async function updateCategory(id: string, name: string) {
  const { data, error } = await supabase
    .from('categories')
    .update({ name })
    .eq('id', id)
    .select()
    .single()

  if (error) throw new Error(error.message)
  return data
}

export async function toggleCategoryHidden(id: string, isHidden: boolean) {
  const { data, error } = await supabase
    .from('categories')
    .update({ is_hidden: isHidden })
    .eq('id', id)
    .select()
    .single()

  if (error) throw new Error(error.message)
  return data
}

export async function updateCategoryColor(id: string, color: string) {
  const { data, error } = await supabase
    .from('categories')
    .update({ color })
    .eq('id', id)
    .is('parent_id', null)
    .select()
    .single()

  if (error) throw new Error(error.message)
  return data
}

export async function updateCategoryParent(id: string, parentId: string) {
  const { error } = await supabase
    .from('categories')
    .update({ parent_id: parentId })
    .eq('id', id)

  if (error) throw new Error(error.message)
}

export async function fetchCategoryUsage(categoryId: string) {
  const [{ count }, { data }] = await Promise.all([
    supabase
      .from('transaction_items')
      .select('*', { count: 'exact', head: true })
      .eq('category_id', categoryId),
    supabase
      .from('transaction_items')
      .select('transactions!inner(date)')
      .eq('category_id', categoryId)
      .order('transactions(date)', { ascending: false })
      .limit(1),
  ])

  const lastUsedDate =
    data?.[0]?.transactions &&
    !Array.isArray(data[0].transactions)
      ? data[0].transactions.date
      : null

  return { count: count ?? 0, lastUsedDate }
}

export async function reorderCategories(
  orders: { id: string; sort_order: number }[],
): Promise<void> {
  if (orders.length === 0) return
  const results = await Promise.all(
    orders.map((o) =>
      supabase.from('categories').update({ sort_order: o.sort_order }).eq('id', o.id),
    ),
  )
  for (const { error } of results) {
    if (error) throw new Error(error.message)
  }
}

export async function reassignTransactionItemsCategory(fromId: string, toId: string | null) {
  const { error } = await supabase
    .from('transaction_items')
    .update({ category_id: toId })
    .eq('category_id', fromId)

  if (error) throw new Error(error.message)
}

export async function deleteCategory(id: string) {
  const { data: category, error: fetchError } = await supabase
    .from('categories')
    .select('id, is_system')
    .eq('id', id)
    .single()

  if (fetchError || !category) throw new Error('Category not found')
  if (category.is_system) throw new Error('System categories cannot be deleted')

  const { error } = await supabase
    .from('categories')
    .delete()
    .eq('id', id)

  if (error) throw new Error(error.message)
}
